import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CalendarIcon, Loader2, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const appointmentSchema = z.object({
  name: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  email: z.string().email("Veuillez entrer une adresse courriel valide"),
  phone: z.string().min(10, "Veuillez entrer un numéro de téléphone valide"),
  preferred_time: z.string().min(1, "Veuillez sélectionner une plage horaire"),
  message: z.string().optional(),
});

const AppointmentCalendar = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateError, setDateError] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(appointmentSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      preferred_time: "",
      message: "",
    },
  });

  const timeSlots = [
    { value: "morning", label: "Matin (9h - 12h)" },
    { value: "afternoon", label: "Après-midi (13h - 17h)" },
    { value: "evening", label: "Soirée (17h - 19h)" },
  ];

  // Disable dates that are not Fridays or Wednesdays
  const isDateDisabled = (date) => {
    const day = date.getDay();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Disable past dates
    if (date < today) return true;
    
    // Only allow Fridays (5) and Wednesdays (3)
    return day !== 3 && day !== 5;
  };

  const onSubmit = async (data) => {
    if (!selectedDate) {
      setDateError("Veuillez sélectionner une date");
      return;
    }
    setDateError("");
    setIsSubmitting(true);
    
    try {
      // Netlify Forms submission
      const formData = new FormData();
      formData.append("form-name", "appointment");
      formData.append("preferred_date", format(selectedDate, "yyyy-MM-dd"));
      Object.keys(data).forEach(key => {
        formData.append(key, data[key]);
      });

      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });

      if (response.ok) {
        setIsSuccess(true);
        toast.success("Demande de rendez-vous envoyée!", {
          description: "Nous vous contacterons pour confirmer.",
        });
        reset();
        setSelectedDate(null);
        
        // Reset success state after 5 seconds
        setTimeout(() => setIsSuccess(false), 5000);
      } else {
        throw new Error("Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error("Appointment form error:", error);
      toast.error("Erreur lors de l'envoi", {
        description: "Veuillez réessayer ou nous appeler directement.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="card text-center py-12" data-testid="appointment-success">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="heading-playfair text-2xl font-semibold mb-2 text-slate-900">
          Demande envoyée!
        </h3>
        <p className="text-slate-600">
          Nous vous contacterons dans les plus brefs délais pour confirmer votre rendez-vous.
        </p>
      </div>
    );
  }

  return (
    <form 
      name="appointment"
      method="POST"
      data-netlify="true"
      onSubmit={handleSubmit(onSubmit)} 
      className="space-y-6" 
      data-testid="appointment-form"
    >
      <input type="hidden" name="form-name" value="appointment" />
      <input type="hidden" name="preferred_date" value={selectedDate ? format(selectedDate, "yyyy-MM-dd") : ""} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="apt-name" className="text-slate-700">
            Nom complet <span className="text-red-500">*</span>
          </Label>
          <Input
            id="apt-name"
            type="text"
            placeholder="Votre nom"
            {...register("name")}
            className={`bg-white border-stone-200 focus:border-slate-900 ${
              errors.name ? "border-red-500" : ""
            }`}
            data-testid="appointment-name-input"
          />
          {errors.name && (
            <p className="text-red-500 text-sm">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="apt-phone" className="text-slate-700">
            Téléphone <span className="text-red-500">*</span>
          </Label>
          <Input
            id="apt-phone"
            type="tel"
            placeholder="450-000-0000"
            {...register("phone")}
            className={`bg-white border-stone-200 focus:border-slate-900 ${
              errors.phone ? "border-red-500" : ""
            }`}
            data-testid="appointment-phone-input"
          />
          {errors.phone && (
            <p className="text-red-500 text-sm">{errors.phone.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="apt-email" className="text-slate-700">
          Courriel <span className="text-red-500">*</span>
        </Label>
        <Input
          id="apt-email"
          type="email"
          placeholder="votre@courriel.com"
          {...register("email")}
          className={`bg-white border-stone-200 focus:border-slate-900 ${
            errors.email ? "border-red-500" : ""
          }`}
          data-testid="appointment-email-input"
        />
        {errors.email && (
          <p className="text-red-500 text-sm">{errors.email.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-slate-700">
            Date préférée <span className="text-red-500">*</span>
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`w-full justify-start text-left font-normal bg-white border-stone-200 hover:bg-stone-50 ${
                  !selectedDate && "text-slate-500"
                } ${dateError ? "border-red-500" : ""}`}
                data-testid="appointment-date-trigger"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? (
                  format(selectedDate, "d MMMM yyyy", { locale: fr })
                ) : (
                  <span>Choisir une date</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  setSelectedDate(date);
                  setDateError("");
                }}
                disabled={isDateDisabled}
                locale={fr}
                initialFocus
                data-testid="appointment-calendar"
              />
              <p className="px-4 pb-3 text-xs text-slate-500">
                Disponible les vendredis et mercredis
              </p>
            </PopoverContent>
          </Popover>
          {dateError && (
            <p className="text-red-500 text-sm">{dateError}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-slate-700">
            Plage horaire <span className="text-red-500">*</span>
          </Label>
          <Select onValueChange={(value) => setValue("preferred_time", value)}>
            <SelectTrigger 
              className={`bg-white border-stone-200 ${
                errors.preferred_time ? "border-red-500" : ""
              }`}
              data-testid="appointment-time-trigger"
            >
              <SelectValue placeholder="Sélectionner une plage" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {timeSlots.map((slot) => (
                <SelectItem 
                  key={slot.value} 
                  value={slot.value}
                  data-testid={`time-slot-${slot.value}`}
                >
                  {slot.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.preferred_time && (
            <p className="text-red-500 text-sm">{errors.preferred_time.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="apt-message" className="text-slate-700">
          Message (optionnel)
        </Label>
        <Textarea
          id="apt-message"
          placeholder="Informations supplémentaires ou questions"
          rows={3}
          {...register("message")}
          className="bg-white border-stone-200 focus:border-slate-900 resize-none"
          data-testid="appointment-message-input"
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full btn-primary py-6"
        data-testid="appointment-submit-button"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Envoi en cours...</span>
          </>
        ) : (
          <span>Demander un rendez-vous</span>
        )}
      </Button>
    </form>
  );
};

export default AppointmentCalendar;
